import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    SelectChangeEvent,
    Slider,
    TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { settingsRoutes } from '../../../../routes';
import {
    buildCreateAiPromptRequest,
    buildUpdateAiPromptRequest,
} from '../../../../sic-lib/api/ai/types/Ai';
import { useCreateAiPrompt, useUpdateAiPrompt } from '../../../../sic-lib/api/hooks/useAiPrompts';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import {
    AiPrompt,
    AiPromptFeatures,
    AiPromptFormState,
    ChatGPTSettings,
    buildAiPromptFormState,
    initialAiPromptFormState,
} from '../../../../sic-lib/types/Ai';
import NavLinkButton from '../../../Navigation/NavLinkButton/NavLinkButton';
import AxiosErrorAlert from '../../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../../Shared/ButtonLoading';

interface Props {
    aiPrompt: AiPrompt | undefined;
    hideBackButton?: boolean;
}

export default function AiCreateEditPrompt({ aiPrompt, hideBackButton = true }: Props) {
    const { classes } = useStyles();
    const history = useHistory();

    const [formState, formDispatch] = React.useReducer<GenericReducer<AiPromptFormState>>(
        genericReducer,
        aiPrompt ? buildAiPromptFormState(aiPrompt) : initialAiPromptFormState,
    );

    const {
        mutate: handleCreate,
        isLoading: isCreating,
        isSuccess: isCreatingSuccess,
        isError: isCreatingError,
        error: createError,
    } = useCreateAiPrompt();

    const {
        mutate: handleSave,
        isLoading: isUpdating,
        isSuccess: isUpdateSuccess,
        isError: isUpdatingError,
        error: updateError,
    } = useUpdateAiPrompt();

    const isLoading = aiPrompt ? isUpdating : isCreating;
    const isSuccess = aiPrompt ? isUpdateSuccess : isCreatingSuccess;
    const isError = aiPrompt ? isUpdatingError : isCreatingError;
    const error = aiPrompt ? updateError : createError;

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let fieldValue: string | boolean | Date;

        switch (event.target.type) {
            case 'checkbox':
                fieldValue = event.target.checked;
                break;
            default:
                fieldValue = event.target.value;
        }

        formDispatch({
            [event.target.name]: fieldValue,
        });
    };

    const handleSliderChange = (event: Event, value: number, name: keyof ChatGPTSettings) => {
        formDispatch({
            gptSettings: {
                ...formState.gptSettings,
                [name]: value,
            },
        });
    };

    const handleFeatureChanged = (event: SelectChangeEvent) => {
        formDispatch({
            [event.target.name || '']:
                AiPromptFeatures[event.target.value as keyof typeof AiPromptFeatures],
        });
    };

    const handleSaveClick = () => {
        if (aiPrompt) {
            handleSave(buildUpdateAiPromptRequest(aiPrompt.promptId, formState));
        } else {
            handleCreate(buildCreateAiPromptRequest(formState), {
                onSuccess: data => {
                    history.push(settingsRoutes.setCreateEditPrompt(data.promptId));
                },
            });
        }
    };

    return (
        <>
            <Paper className={classes.root}>
                <Grid
                    className={classes.header}
                    container
                    flexDirection="row"
                    justifyContent="space-between"
                >
                    <Grid item>
                        <Typography component="h3" variant="h6">
                            {aiPrompt ? 'Edit' : 'Create'} Prompt
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Grid container flexDirection="row" spacing={2}>
                            {!hideBackButton && (
                                <Grid item>
                                    <NavLinkButton pathname={settingsRoutes.aiPrompts}>
                                        Back to All Prompts
                                    </NavLinkButton>
                                </Grid>
                            )}
                            <Grid item>
                                <ButtonLoading
                                    variant="contained"
                                    color="primary"
                                    isLoading={isLoading}
                                    disabled={isLoading}
                                    onClick={handleSaveClick}
                                    isError={isError}
                                    isSuccess={isSuccess}
                                    startIcon={<SaveIcon />}
                                >
                                    Save
                                </ButtonLoading>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    container
                    className={classes.list}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box mb={2}>
                        <AxiosErrorAlert error={error} />
                    </Box>
                    <FormControl fullWidth>
                        <TextField
                            id="title"
                            name="title"
                            label="Title"
                            variant="outlined"
                            value={formState.title}
                            onChange={handleInputChange}
                            multiline
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <TextField
                            id="description"
                            name="description"
                            label="Description"
                            variant="outlined"
                            value={formState.description}
                            onChange={handleInputChange}
                            multiline
                        />
                    </FormControl>
                    {/* <FormControl fullWidth>
                        <TextField
                            id="category"
                            name="category"
                            label="Category (not yet utilized...)"
                            variant="outlined"
                            value={formState.category}
                            onChange={handleInputChange}
                            multiline
                        />
                    </FormControl> */}
                    {!aiPrompt && (
                        <FormControl fullWidth>
                            <InputLabel id="formState.feature-label">Feature</InputLabel>
                            <Select
                                name="feature"
                                labelId="formState.feature-label"
                                id="formState.feature-select"
                                value={AiPromptFeatures[formState.feature]}
                                label="Feature"
                                onChange={handleFeatureChanged}
                            >
                                {Object.keys(AiPromptFeatures)
                                    .filter(key => isNaN(Number(key)))
                                    .filter(key => key !== 'SmartReply')
                                    .map(key => (
                                        <MenuItem value={key} key={key}>
                                            {key}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
                    <FormControl fullWidth>
                        <TextField
                            id="clientId"
                            name="clientId"
                            label="ClientId (blank for global)"
                            variant="outlined"
                            value={formState.clientId}
                            onChange={handleInputChange}
                            multiline
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <TextField
                            id="userName"
                            name="userName"
                            label="User Name (blank for all users on a client)"
                            variant="outlined"
                            value={formState.userName}
                            onChange={handleInputChange}
                            disabled={!formState.clientId}
                            multiline
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <TextField
                            id="prompt"
                            name="prompt"
                            label="Prompt"
                            variant="outlined"
                            value={formState.prompt}
                            onChange={handleInputChange}
                            multiline
                        />
                    </FormControl>
                    {formState.feature !== AiPromptFeatures.Transcript && (
                        <>
                            <Typography variant="h6">AI Customization</Typography>
                            <Link
                                href="https://platform.openai.com/docs/api-reference/chat/create"
                                target="_blank"
                            >
                                Read about ChatGPT's options...
                            </Link>
                            <FormControl fullWidth></FormControl>
                            <FormControl fullWidth>
                                <Typography gutterBottom>
                                    temperature: {formState.gptSettings.temperature}
                                </Typography>
                                <Slider
                                    id="temperature"
                                    name="temperature"
                                    aria-label="temperature"
                                    valueLabelDisplay="auto"
                                    value={formState.gptSettings.temperature}
                                    step={0.1}
                                    min={0}
                                    max={2}
                                    marks
                                    onChange={(event, number) =>
                                        handleSliderChange(event, number as number, 'temperature')
                                    }
                                />
                            </FormControl>
                            <FormControl fullWidth>
                                <Typography gutterBottom>
                                    topP: {formState.gptSettings.topP}
                                </Typography>
                                <Slider
                                    id="topP"
                                    name="topP"
                                    aria-label="topP"
                                    valueLabelDisplay="auto"
                                    value={formState.gptSettings.topP}
                                    step={0.1}
                                    min={0}
                                    max={1}
                                    marks
                                    onChange={(event, number) =>
                                        handleSliderChange(event, number as number, 'topP')
                                    }
                                />
                            </FormControl>
                            <FormControl fullWidth>
                                <Typography gutterBottom>
                                    presencePenalty: {formState.gptSettings.presencePenalty}
                                </Typography>
                                <Slider
                                    id="presencePenalty"
                                    name="presencePenalty"
                                    aria-label="presencePenalty"
                                    valueLabelDisplay="auto"
                                    value={formState.gptSettings.presencePenalty}
                                    step={0.1}
                                    min={-2}
                                    max={2}
                                    marks
                                    onChange={(event, number) =>
                                        handleSliderChange(
                                            event,
                                            number as number,
                                            'presencePenalty',
                                        )
                                    }
                                />
                            </FormControl>
                            <FormControl fullWidth>
                                <Typography gutterBottom>
                                    frequencyPenalty: {formState.gptSettings.frequencyPenalty}
                                </Typography>
                                <Slider
                                    id="frequencyPenalty"
                                    name="frequencyPenalty"
                                    aria-label="frequencyPenalty"
                                    valueLabelDisplay="auto"
                                    value={formState.gptSettings.frequencyPenalty}
                                    step={0.1}
                                    min={-2}
                                    max={2}
                                    marks
                                    onChange={(event, number) =>
                                        handleSliderChange(
                                            event,
                                            number as number,
                                            'frequencyPenalty',
                                        )
                                    }
                                />
                            </FormControl>
                            {formState.feature !== AiPromptFeatures.SmartReply && (
                                <>
                                    <FormControl fullWidth>
                                        <TextField
                                            id="serializedFunctions"
                                            name="serializedFunctions"
                                            label="GptFunctions (very beta, 'type: 'array'' does not work yet)"
                                            variant="outlined"
                                            value={formState.serializedFunctions}
                                            onChange={handleInputChange}
                                            multiline
                                        />
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <TextField
                                            id="summaryFunctionKeysCsv"
                                            name="summaryFunctionKeysCsv"
                                            label="Summary Function Keys (comma separated)"
                                            variant="outlined"
                                            value={formState.summaryFunctionKeysCsv}
                                            onChange={handleInputChange}
                                            multiline
                                        />
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <TextField
                                            id="timelineFunctionKeysCsv"
                                            name="timelineFunctionKeysCsv"
                                            label="Timeline Function Keys (comma separated)"
                                            variant="outlined"
                                            value={formState.timelineFunctionKeysCsv}
                                            onChange={handleInputChange}
                                            multiline
                                        />
                                    </FormControl>
                                </>
                            )}
                        </>
                    )}
                </Grid>
            </Paper>
        </>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        marginBottom: theme.spacing(1),
        '& .MuiFormControl-root': {
            marginBottom: theme.spacing(1),
        },
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        position: 'relative',
        padding: theme.spacing(2),
        '& > h3': {
            textTransform: 'capitalize',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            display: 'block',
            width: '100%',
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
    },
    list: {
        padding: theme.spacing(2),
    },
    linkText: {
        //color: theme.palette.common.white
    },
    error: {
        color: 'red',
        padding: theme.spacing(2),
    },
}));
